import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    DndContext,
    closestCenter,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    horizontalListSortingStrategy,
    arrayMove,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {apiFetch} from '../api';
import Checklist from './Checklist';
import NewChecklistModal from './NewChecklistModal';
import GroupSidebar from './GroupSidebar';
import TemplateContainer from './TemplateContainer';

const DATE_VIEWS = ['overdue', 'today', 'tomorrow', 'next7days'];

function getLocalDateStr(date) {
    return date.toLocaleDateString('en-CA');
}

function SortableChecklist({checklist, isDragEnabled, ...props}) {
    const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({id: checklist.id, disabled: !isDragEnabled});
    const {role, 'aria-disabled': _ariaDisabled, ...safeAttrs} = attributes;
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : undefined,
        opacity: isDragging ? 0.5 : 1,
    };
    return (
        <div ref={setNodeRef} style={style} className="sortable-checklist-wrapper" id={`checklist-${checklist.id}`} {...safeAttrs}>
            <Checklist data={checklist} dragListeners={isDragEnabled ? listeners : null} isDragEnabled={isDragEnabled} {...props} />
        </div>
    );
}

export default function ChecklistContainer() {
    const [checklistData, setChecklistData] = useState([]);
    const [groups, setGroups] = useState([]);
    const [selectedGroupId, setSelectedGroupId] = useState('all');
    const [showModal, setShowModal] = useState(false);
    const [modalTemplates, setModalTemplates] = useState([]);
    const [focusedId, setFocusedId] = useState(null);
    const [innerView, setInnerView] = useState('checklists');

    useEffect(() => {
        if (!focusedId) return;
        const handleKeyDown = (e) => { if (e.key === 'Escape') setFocusedId(null); };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [focusedId]);

    useEffect(() => {
        document.body.classList.toggle('focus-mode', focusedId !== null);
        return () => document.body.classList.remove('focus-mode');
    }, [focusedId]);

    const focusChecklist = useCallback((id) => setFocusedId(id), []);
    const unfocusChecklist = useCallback(() => setFocusedId(null), []);

    const dataRef = useRef(checklistData);
    useEffect(() => { dataRef.current = checklistData; }, [checklistData]);

    useEffect(() => {
        const controller = new AbortController();
        apiFetch('/checklists', {signal: controller.signal})
            .then(res => res.json())
            .then(data => setChecklistData(data))
            .catch(err => { if (err.name !== 'AbortError') throw err; });
        apiFetch('/checklist-groups', {signal: controller.signal})
            .then(res => res.json())
            .then(data => setGroups(data))
            .catch(err => { if (err.name !== 'AbortError') throw err; });
        return () => controller.abort();
    }, []);

    const isReadOnlyView = selectedGroupId === 'starred' || DATE_VIEWS.includes(selectedGroupId);
    const isReorderable = !isReadOnlyView && selectedGroupId !== 'all';

    const visibleChecklists = useMemo(() => {
        if (selectedGroupId === 'all') return checklistData;
        if (selectedGroupId === 'starred') {
            return checklistData
                .filter(c => c.items.some(i => i.starred))
                .map(c => ({...c, items: c.items.filter(i => i.starred)}));
        }
        if (DATE_VIEWS.includes(selectedGroupId)) {
            const today = getLocalDateStr(new Date());
            const tomorrow = getLocalDateStr(new Date(Date.now() + 86400000));
            const in7Days = getLocalDateStr(new Date(Date.now() + 7 * 86400000));
            const filters = {
                overdue:   i => !i.checked && i.dueDate && i.dueDate < today,
                today:     i => !i.checked && i.dueDate === today,
                tomorrow:  i => !i.checked && i.dueDate === tomorrow,
                next7days: i => !i.checked && i.dueDate > tomorrow && i.dueDate <= in7Days,
            };
            const fn = filters[selectedGroupId];
            return checklistData
                .filter(c => c.items.some(fn))
                .map(c => ({...c, items: c.items.filter(fn)}));
        }
        if (selectedGroupId === null) return checklistData.filter(c => c.groupId == null);
        return checklistData.filter(c => c.groupId === selectedGroupId);
    }, [checklistData, selectedGroupId]);

    const putChecklist = useCallback(async (checklistId, updated) => {
        const res = await apiFetch(`/checklists/${checklistId}`, {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(updated),
        });
        const saved = await res.json();
        setChecklistData(prev => prev.map(c => c.id === checklistId ? saved : c));
    }, []);

    const deleteChecklist = useCallback(async (id) => {
        await apiFetch(`/checklists/${id}`, {method: 'DELETE'});
        setChecklistData(prev => prev.filter(c => c.id !== id));
        setFocusedId(prev => prev === id ? null : prev);
    }, []);

    const updateTitle = useCallback((checklistId, title) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        putChecklist(checklistId, {...checklist, title});
    }, [putChecklist]);

    const updateItem = useCallback(async (checklistId, itemId, title) => {
        const res = await apiFetch(`/checklists/${checklistId}/items/${itemId}`, {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({title}),
        });
        const updated = await res.json();
        setChecklistData(prev => prev.map(c =>
            c.id === checklistId ? {...c, items: c.items.map(i => i.id === itemId ? updated : i)} : c
        ));
    }, []);

    const deleteItem = useCallback(async (checklistId, itemId) => {
        await apiFetch(`/checklists/${checklistId}/items/${itemId}`, {method: 'DELETE'});
        setChecklistData(prev => prev.map(c =>
            c.id === checklistId ? {...c, items: c.items.filter(i => i.id !== itemId)} : c
        ));
    }, []);

    const addItem = useCallback(async (checklistId, title) => {
        const res = await apiFetch(`/checklists/${checklistId}/items`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({title}),
        });
        const newItem = await res.json();
        setChecklistData(prev => prev.map(c =>
            c.id === checklistId ? {...c, items: [newItem, ...c.items]} : c
        ));
    }, []);

    const starItem = useCallback(async (checklistId, itemId) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        const item = checklist.items.find(i => i.id === itemId);
        const res = await apiFetch(`/checklists/${checklistId}/items/${itemId}`, {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({starred: !item.starred}),
        });
        const updated = await res.json();
        setChecklistData(prev => prev.map(c =>
            c.id === checklistId ? {...c, items: c.items.map(i => i.id === itemId ? updated : i)} : c
        ));
    }, []);

    const setDueDate = useCallback(async (checklistId, itemId, dueDate) => {
        const res = await apiFetch(`/checklists/${checklistId}/items/${itemId}`, {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({dueDate}),
        });
        const updated = await res.json();
        setChecklistData(prev => prev.map(c =>
            c.id === checklistId ? {...c, items: c.items.map(i => i.id === itemId ? updated : i)} : c
        ));
    }, []);

    const reorderItems = useCallback((checklistId, movedItemId, afterId, newItems) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        const updated = {...checklist, items: newItems, sortMode: 'custom'};
        setChecklistData(prev => prev.map(c => c.id === checklistId ? updated : c));
        apiFetch(`/checklists/${checklistId}/items/${movedItemId}`, {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({after: afterId}),
        });
        if (checklist.sortMode !== 'custom') {
            apiFetch(`/checklists/${checklistId}`, {
                method: 'PUT',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({...checklist, sortMode: 'custom'}),
            });
        }
    }, []);

    const reorderChecklists = useCallback((movedId, afterId, reorderedGroup) => {
        setChecklistData(prev => {
            const groupIds = new Set(reorderedGroup.map(c => c.id));
            const result = [];
            let ri = 0;
            for (const c of prev) {
                result.push(groupIds.has(c.id) ? reorderedGroup[ri++] : c);
            }
            return result;
        });
        apiFetch(`/checklists/${movedId}`, {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({after: afterId}),
        });
    }, []);

    const toggleSinkChecked = useCallback((checklistId) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        const newVal = checklist.sinkCheckedItems === false;
        putChecklist(checklistId, {...checklist, sinkCheckedItems: newVal});
    }, [putChecklist]);

    const toggleHideChecked = useCallback((checklistId) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        const newVal = !checklist.hideCheckedItems;
        putChecklist(checklistId, {...checklist, hideCheckedItems: newVal});
    }, [putChecklist]);

    const toggleSortMode = useCallback((checklistId) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        if (checklist.sortMode === 'custom') {
            putChecklist(checklistId, {...checklist, sortMode: 'created_at'});
        } else {
            const sorted = [...checklist.items].sort((a, b) => a.position - b.position);
            putChecklist(checklistId, {...checklist, items: sorted, sortMode: 'custom'});
        }
    }, [putChecklist]);

    const toggleItem = useCallback(async (checklistId, itemId) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        const item = checklist.items.find(i => i.id === itemId);
        const res = await apiFetch(`/checklists/${checklistId}/items/${itemId}`, {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({checked: !item.checked}),
        });
        const updated = await res.json();
        setChecklistData(prev => prev.map(c =>
            c.id === checklistId ? {...c, items: c.items.map(i => i.id === itemId ? updated : i)} : c
        ));
    }, []);

    const addNewChecklist = useCallback(async (title) => {
        const body = {title, items: []};
        if (selectedGroupId !== 'all') {
            body.groupId = selectedGroupId;
        }
        const res = await apiFetch('/checklists', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(body),
        });
        const created = await res.json();
        setChecklistData(prev => [...prev, created]);
        setFocusedId(created.id);
    }, [selectedGroupId]);

    const openNewListModal = useCallback(async () => {
        const res = await apiFetch('/checklist-templates');
        const templates = await res.json();
        setModalTemplates(templates);
        setShowModal(true);
    }, []);

    const handleModalConfirm = useCallback(async (title, templateId) => {
        setShowModal(false);
        if (templateId) {
            const res = await apiFetch(`/checklist-templates/${templateId}/spawn`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({name: title}),
            });
            const created = await res.json();
            setChecklistData(prev => [...prev, created]);
            setFocusedId(created.id);
        } else {
            addNewChecklist(title);
        }
    }, [addNewChecklist]);

    const renameGroup = useCallback(async (id, name) => {
        await apiFetch(`/checklist-groups/${id}`, {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({name}),
        });
        setGroups(prev => prev.map(g => g.id === id ? {...g, name} : g));
    }, []);

    const createGroup = useCallback(async (name) => {
        const res = await apiFetch('/checklist-groups', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({name}),
        });
        const group = await res.json();
        setGroups(prev => [...prev, group]);
    }, []);

    const deleteGroup = useCallback(async (id) => {
        const res = await apiFetch(`/checklist-groups/${id}`, {method: 'DELETE'});
        if (!res.ok) return;
        setGroups(prev => prev.filter(g => g.id !== id));
        setSelectedGroupId(prev => prev === id ? 'all' : prev);
    }, []);

    const checklistSensors = useSensors(
        useSensor(PointerSensor, {activationConstraint: {distance: 8}}),
        useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates}),
    );

    function handleChecklistDragEnd(event) {
        const {active, over} = event;
        if (!over || active.id === over.id) return;
        const oldIndex = visibleChecklists.findIndex(c => c.id === active.id);
        const newIndex = visibleChecklists.findIndex(c => c.id === over.id);
        const reordered = arrayMove(visibleChecklists, oldIndex, newIndex);
        const afterId = newIndex > 0 ? reordered[newIndex - 1].id : null;
        reorderChecklists(active.id, afterId, reordered);
    }

    return (
        <div className="checklist-layout">
            <GroupSidebar
                groups={groups}
                selectedGroupId={selectedGroupId}
                checklistData={checklistData}
                onSelect={(id) => { setInnerView('checklists'); setSelectedGroupId(id); }}
                onAddGroup={createGroup}
                onRenameGroup={renameGroup}
                onDeleteGroup={deleteGroup}
                onTemplates={() => { setInnerView('templates'); setSelectedGroupId(undefined); }}
                isTemplatesActive={innerView === 'templates'}
                hasDueDateItems={checklistData.some(c => c.items.some(i => i.dueDate))}
            />
            {innerView === 'templates' ? (
                <TemplateContainer onSpawn={(checklist) => {
                    setChecklistData(prev => [...prev, checklist]);
                    setInnerView('checklists');
                    setSelectedGroupId(checklist.groupId || null);
                    setTimeout(() => {
                        const el = document.getElementById(`checklist-${checklist.id}`);
                        if (el) el.scrollIntoView({behavior: 'smooth', block: 'center'});
                    }, 100);
                }} />
            ) : (
                <div className="checklist-container-root">
                    {!isReadOnlyView && (
                        <div>
                            <button className="add-checklist-button" onClick={openNewListModal}>
                                + New List
                            </button>
                        </div>
                    )}
                    {showModal ? (
                        <NewChecklistModal onConfirm={handleModalConfirm} onCancel={() => setShowModal(false)} templates={modalTemplates}/>
                    ) : null}
                    {focusedId !== null && (
                        <div className="focus-backdrop" onClick={() => setFocusedId(null)} />
                    )}
                    <DndContext sensors={checklistSensors} collisionDetection={closestCenter} onDragEnd={handleChecklistDragEnd}>
                        <SortableContext items={visibleChecklists.map(c => c.id)} strategy={horizontalListSortingStrategy}>
                            <div className={`checklists-container${focusedId !== null ? ' checklists-container--hidden' : ''}`}>
                                {visibleChecklists.length === 0 && (
                                    <div className="empty-state">
                                        <p className="empty-state-text">
                                            {selectedGroupId === 'starred'
                                                ? 'Zero stars. Your tasks have the charisma of a wet napkin.'
                                                : selectedGroupId === 'overdue'
                                                    ? 'Nothing overdue. Suspicious. Are you even trying?'
                                                    : selectedGroupId === 'today'
                                                        ? 'Nothing due today. The universe clearly made a clerical error.'
                                                        : selectedGroupId === 'tomorrow'
                                                            ? 'Tomorrow is blissfully empty. Future you will ruin this.'
                                                            : DATE_VIEWS.includes(selectedGroupId)
                                                                ? 'No deadlines this week. Go touch grass or something.'
                                                                : 'This group is emptier than your fridge on a Sunday night.'}
                                        </p>
                                    </div>
                                )}
                                {visibleChecklists.map(checklist => (
                                    <SortableChecklist
                                        key={checklist.id}
                                        checklist={checklist}
                                        isDragEnabled={isReorderable}
                                        onDelete={deleteChecklist}
                                        onAddItem={addItem}
                                        onToggleItem={toggleItem}
                                        onDeleteItem={deleteItem}
                                        onUpdateTitle={updateTitle}
                                        onUpdateItem={updateItem}
                                        onReorderItems={reorderItems}
                                        onToggleSortMode={toggleSortMode}
                                        onToggleSinkChecked={toggleSinkChecked}
                                        onToggleHideChecked={toggleHideChecked}
                                        onStarItem={starItem}
                                        onSetDueDateItem={setDueDate}
                                        readOnly={isReadOnlyView}
                                        isFocused={focusedId === checklist.id}
                                        onFocus={focusChecklist}
                                        onUnfocus={unfocusChecklist}
                                    />
                                ))}
                            </div>
                        </SortableContext>
                    </DndContext>
                </div>
            )}
        </div>
    );
}
